// ─────────────────────────────────────────────
// services/waitlistService.js
// Client-side waitlist integration.
// Triggers allocator runs and provides join/claim helpers.
//
// All actual queue logic lives server-side in edge functions.
// This file is the thin integration layer.
//
// Original IP — LoadDash Services. All rights reserved.
// ─────────────────────────────────────────────

import { supabase } from "./supabaseService";
import { getLEU } from "../constants";

/**
 * Trigger the waitlist allocator for a specific zone + pickup_day.
 * Called after cancellations, declines, admin removals, load downgrades.
 *
 * @param {string} zone - Zone key (A, B, C, D)
 * @param {string} pickupDay - ISO date string (YYYY-MM-DD)
 * @param {string} trigger - What caused this run (e.g. "cancellation", "decline")
 * @param {string} [token] - Optional auth token for admin-triggered runs
 */
export async function triggerAllocator(zone, pickupDay, trigger, token) {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const { data, error } = await supabase.functions.invoke(
      "waitlist-allocator",
      {
        body: { zone, pickup_day: pickupDay, trigger },
        headers,
      }
    );
    if (error) {
      console.debug("[Waitlist] Allocator trigger failed:", error.message);
      return null;
    }
    return data;
  } catch (e) {
    console.debug("[Waitlist] Allocator trigger error:", e?.message);
    return null;
  }
}

/**
 * Join the waitlist for a specific zone + pickup_day.
 * Creates a waitlist_entries row via direct insert (service handles dedup).
 *
 * @param {object} params
 * @param {string} params.userId
 * @param {string} params.contactKey - Normalized phone or email
 * @param {string} params.zone
 * @param {string} params.pickupDay - ISO date string
 * @param {string} params.loadSizeKey - e.g. "Small", "Half"
 * @param {string} [params.requestId] - Optional linked request
 * @param {number} [params.price] - Booking price for scoring
 * @param {number} [params.priorityScore] - Optional priority override
 */
export async function joinWaitlist({
  userId,
  contactKey,
  zone,
  pickupDay,
  loadSizeKey,
  requestId = null,
  price = null,
  priorityScore = 0,
}) {
  const requestedLeu = getLEU(loadSizeKey);

  // Convert display-formatted date to ISO (YYYY-MM-DD) for PostgreSQL date column
  const parsedDate = new Date(pickupDay);
  const isoDay = parsedDate.toISOString().split("T")[0];
  if (!isoDay || isNaN(parsedDate.getTime())) {
    throw new Error("WAITLIST_DATE_INVALID: Cannot parse pickup day: " + pickupDay);
  }

  const { data, error } = await supabase.from("waitlist_entries").insert([
    {
      user_id: userId,
      contact_key: contactKey,
      zone,
      pickup_day: isoDay,
      load_size_key: loadSizeKey,
      requested_leu: requestedLeu,
      request_id: requestId,
      price: price,
      priority_score: priorityScore,
      offer_state: "waiting",
    },
  ]).select().single();

  if (error) {
    // Duplicate active entry returns a unique constraint violation
    if (error.code === "23505") {
      return { data: null, error: "already_on_waitlist" };
    }
    return { data: null, error: error.message };
  }
  return { data, error: null };
}

/**
 * Claim a waitlist offer using the offer token.
 *
 * @param {string} offerToken
 */
export async function claimOffer(offerToken) {
  try {
    const { data, error } = await supabase.functions.invoke(
      "waitlist-claim",
      { body: { offer_token: offerToken } }
    );
    if (error) return { data: null, error: error.message };
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message || "Claim failed" };
  }
}

/**
 * Decline a waitlist offer.
 * Moves the entry to declined state so the queue advances.
 *
 * @param {string} offerToken
 */
export async function declineOffer(offerToken) {
  try {
    const { data, error } = await supabase.functions.invoke(
      "waitlist-claim",
      { body: { offer_token: offerToken, action: "decline" } }
    );
    if (error) return { success: false, error: error.message };
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e?.message || "Decline failed" };
  }
}

/**
 * Get waitlist status for a user on a specific zone + day.
 * Returns the active entry if one exists.
 *
 * @param {string} userId
 * @param {string} zone
 * @param {string} pickupDay
 */
export async function getWaitlistStatus(userId, zone, pickupDay) {
  const { data, error } = await supabase
    .from("waitlist_entries")
    .select("id, offer_state, created_at, notified_at, expires_at, offer_token")
    .eq("user_id", userId)
    .eq("zone", zone)
    .eq("pickup_day", pickupDay)
    .in("offer_state", ["waiting", "notified", "notify_failed"])
    .single();

  return { data: data || null, error: error?.message || null };
}

/**
 * Run the cleanup job (admin-only).
 * @param {string} [token] - Admin auth token
 */
export async function runCleanup(token) {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const { data, error } = await supabase.functions.invoke(
      "waitlist-cleanup",
      { body: {}, headers }
    );
    if (error) return { data: null, error: error.message };
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message || "Cleanup failed" };
  }
}
